import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { lockedLessonGuard } from "#/modules/les.leerling-gestuurd/middleware.locked-lesson";
import { Result } from "better-result";
import { z } from "zod/v4";
import { getLesSportdeelnames } from "#/modules/sportdeelname/api.sportdeelname.server";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import {
  annuleerLes,
  getDocentGestuurdeLes,
  getLesadministraties,
  getLesEditData,
  getLesInformatie,
  getLesNotities,
  getLesResultaten,
  lesAfronden,
  lesAfrondenPrecheck,
  redistributeLesgroepen,
  startLesson,
  toggleLesAvatars,
  updateBeweegresultaatDocent,
  updateBeweegresultaatOpmerking,
  updateLesadministratieAanwezigheid,
  updateLesadministratieGymkleding,
  updateLesadministratieOpmerking,
  updateLesadministratieTimeoutType,
  updateLesNotitie,
  updateSociaalEmotioneelResultaatDocent,
  updateSociaalEmotioneelResultaatOpmerking,
} from "#/modules/les.docent-gestuurd/api.les.docent.server";

const lesIdSchema = z.object({ lesId: z.string() });

/**
 * Teacher lesson layout data. Mirrors portal's `teacher.tsx` loader: fetches the docent-gestuurde
 * les + edit data in parallel. On a 404 it signals `notFound` so the route can redirect to the
 * lessons overview.
 */
export const getTeacherLessonData = createServerFn({ method: "GET" })
  .middleware([authMiddleware, lockedLessonGuard])
  .validator((data: { lesId: string }) => lesIdSchema.parse(data))
  .handler(async ({ data }) => {
    const [lessonResult, lesEditDataResult] = await Promise.all([
      getDocentGestuurdeLes(data.lesId),
      getLesEditData(data.lesId),
    ]);

    if (Result.isError(lessonResult)) {
      if (lessonResult.error.status === 404) {
        return { lesson: null, lesEditData: null, notFound: true as const };
      }
      throw lessonResult.error;
    }

    if (Result.isError(lesEditDataResult)) {
      throw lesEditDataResult.error;
    }

    return {
      lesson: lessonResult.value,
      lesEditData: lesEditDataResult.value,
      notFound: false as const,
    };
  });

export const getTeacherLesResultaten = createServerFn({ method: "GET" })
  .middleware([authMiddleware, lockedLessonGuard])
  .validator((data: { lesId: string }) => lesIdSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await getLesResultaten(data.lesId);
    if (Result.isError(result)) throw result.error;
    return { resultaten: result.value };
  });

export const getTeacherLesadministraties = createServerFn({ method: "GET" })
  .middleware([authMiddleware, lockedLessonGuard])
  .validator((data: { lesId: string }) => lesIdSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await getLesadministraties(data.lesId);
    if (Result.isError(result)) throw result.error;
    return { lesadministraties: result.value };
  });

export const getTeacherLesInformatie = createServerFn({ method: "GET" })
  .middleware([authMiddleware, lockedLessonGuard])
  .validator((data: { lesId: string }) => lesIdSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await getLesInformatie(data.lesId);
    if (Result.isError(result)) throw result.error;
    return { lesInformatie: result.value };
  });

export const getTeacherLesNotities = createServerFn({ method: "GET" })
  .middleware([authMiddleware, lockedLessonGuard])
  .validator((data: { lesId: string }) => lesIdSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await getLesNotities(data.lesId);
    if (Result.isError(result)) throw result.error;
    return { notities: result.value };
  });

export const getTeacherSportdeelnames = createServerFn({ method: "GET" })
  .middleware([authMiddleware, lockedLessonGuard])
  .validator((data: { lesId: string }) => lesIdSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await getLesSportdeelnames(data.lesId);
    if (Result.isError(result)) {
      throw result.error;
    }
    return { data: result.value };
  });

/* ---------------------------------------------------------------------------------------------- *
 * Mutations. Portal used route `action`s hit through fetchers with an artificial `delay(2000)`
 * `clientAction`; here the field components call these POST server fns via react-query `useMutation`
 * and `router.invalidate()` on success (see the field components). The dev-only 2s latency hack is
 * intentionally dropped.
 * ---------------------------------------------------------------------------------------------- */

export const toggleAvatars = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator((data: { lesId: string }) => lesIdSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await toggleLesAvatars(data.lesId);
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

const updateBeweegresultaatSchema = z.object({
  lesId: z.string(),
  leerlingId: z.string(),
  beweegactiviteitId: z.string(),
  niveau: z.number().nullish(),
  waarde: z.number().nullish(),
  beweegdoelIds: z.array(z.string()).nullish(),
});

export const updateBeweegresultaat = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(updateBeweegresultaatSchema)
  .handler(async ({ data }) => {
    const result = await updateBeweegresultaatDocent({
      lesId: data.lesId,
      leerlingId: data.leerlingId,
      beweegactiviteitId: data.beweegactiviteitId,
      niveau: data.niveau ?? undefined,
      waarde: data.waarde ?? undefined,
      beweegdoelIds: data.beweegdoelIds ?? undefined,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

const updateBeweegresultaatOpmerkingSchema = z.object({
  lesId: z.string(),
  leerlingId: z.string(),
  beweegactiviteitId: z.string(),
  opmerking: z.string().nullish(),
});

export const updateBeweegresultaatOpmerkingFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(updateBeweegresultaatOpmerkingSchema)
  .handler(async ({ data }) => {
    const result = await updateBeweegresultaatOpmerking({
      lesId: data.lesId,
      leerlingId: data.leerlingId,
      beweegactiviteitId: data.beweegactiviteitId,
      opmerking: data.opmerking?.trim() ? data.opmerking.trim() : null,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

const updateSociaalEmotioneelResultaatSchema = z.object({
  lesId: z.string(),
  leerlingId: z.string(),
  sociaalEmotioneelThemaId: z.string(),
  sociaalEmotioneelDoelIds: z.array(z.string()),
});

export const updateSociaalEmotioneelResultaat = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(updateSociaalEmotioneelResultaatSchema)
  .handler(async ({ data }) => {
    const result = await updateSociaalEmotioneelResultaatDocent({
      lesId: data.lesId,
      leerlingId: data.leerlingId,
      sociaalEmotioneelThemaId: data.sociaalEmotioneelThemaId,
      sociaalEmotioneelDoelIds: data.sociaalEmotioneelDoelIds,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

const updateSociaalEmotioneelResultaatOpmerkingSchema = z.object({
  lesId: z.string(),
  leerlingId: z.string(),
  sociaalEmotioneelThemaId: z.string(),
  opmerking: z.string().nullish(),
});

export const updateSociaalEmotioneelResultaatOpmerkingFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(updateSociaalEmotioneelResultaatOpmerkingSchema)
  .handler(async ({ data }) => {
    const result = await updateSociaalEmotioneelResultaatOpmerking({
      lesId: data.lesId,
      leerlingId: data.leerlingId,
      sociaalEmotioneelThemaId: data.sociaalEmotioneelThemaId,
      opmerking: data.opmerking?.trim() ? data.opmerking.trim() : null,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

const updateAanwezigheidSchema = z.object({
  lesId: z.string(),
  leerlingId: z.string(),
  aanwezigheid: z.enum(["Aanwezig", "TeLaat", "Ziek", "Geblesseerd", "Anders"]),
});

export const updateLesadministratieAanwezigheidFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(updateAanwezigheidSchema)
  .handler(async ({ data }) => {
    const result = await updateLesadministratieAanwezigheid({
      lesId: data.lesId,
      leerlingId: data.leerlingId,
      aanwezigheid: data.aanwezigheid,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

export const redistributeLesgroepenFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(lesIdSchema)
  .handler(async ({ data }) => {
    const result = await redistributeLesgroepen({ lesId: data.lesId });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

const updateGymkledingSchema = z.object({
  lesId: z.string(),
  leerlingId: z.string(),
  gymkleding: z.boolean(),
});

export const updateLesadministratieGymkledingFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(updateGymkledingSchema)
  .handler(async ({ data }) => {
    const result = await updateLesadministratieGymkleding({
      lesId: data.lesId,
      leerlingId: data.leerlingId,
      gymkleding: data.gymkleding,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

const updateTimeoutTypeSchema = z.object({
  lesId: z.string(),
  leerlingId: z.string(),
  timeoutType: z.enum(["Geen", "KorteTimeout", "LangeTimeout", "UitsluitingLes"]),
});

export const updateLesadministratieTimeoutTypeFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(updateTimeoutTypeSchema)
  .handler(async ({ data }) => {
    const result = await updateLesadministratieTimeoutType({
      lesId: data.lesId,
      leerlingId: data.leerlingId,
      timeoutType: data.timeoutType,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

const updateLesadministratieOpmerkingSchema = z.object({
  lesId: z.string(),
  leerlingId: z.string(),
  opmerking: z.string().nullish(),
});

export const updateLesadministratieOpmerkingFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(updateLesadministratieOpmerkingSchema)
  .handler(async ({ data }) => {
    const result = await updateLesadministratieOpmerking({
      lesId: data.lesId,
      leerlingId: data.leerlingId,
      opmerking: data.opmerking?.trim() ? data.opmerking.trim() : null,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

const updateNotitieSchema = z.object({
  lesId: z.string(),
  notitie: z.string().nullish(),
});

export const updateNotitie = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(updateNotitieSchema)
  .handler(async ({ data }) => {
    const result = await updateLesNotitie({
      lesId: data.lesId,
      notitie: data.notitie || undefined,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

/* ---------------------------------------------------------------------------------------------- *
 * Lesson lifecycle actions (portal's teacher-layout fetcher intents: start / annuleer / afronden).
 * ---------------------------------------------------------------------------------------------- */

const lifecycleSchema = z.object({
  lesId: z.string(),
  timetravel: z.boolean().optional(),
});

export const startLes = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(lifecycleSchema)
  .handler(async ({ data }) => {
    const result = await startLesson({
      lesId: data.lesId,
      timetravel: data.timetravel ?? false,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

export const annuleerLesFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator((data: { lesId: string }) => lesIdSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await annuleerLes({ lesId: data.lesId });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const };
  });

export const lesAfrondenPrecheckFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator((data: { lesId: string }) => lesIdSchema.parse(data))
  .handler(async ({ data }) => {
    const result = await lesAfrondenPrecheck({ lesId: data.lesId });
    if (Result.isError(result)) return apiFailure(result.error);
    return result.value;
  });

export const lesAfrondenFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, lockedLessonGuard, toastErrorMiddleware])
  .validator(lifecycleSchema)
  .handler(async ({ data }) => {
    const result = await lesAfronden({
      lesId: data.lesId,
      timetravel: data.timetravel ?? false,
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return { success: true as const, data: result.value };
  });
